import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Pencil, Save, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { pricingApi } from "@/lib/adminApi";

const BASIS_LABEL = { FIXED_RANGE: "Fixed", HOURLY: "Hourly" };

export function PricingRegister() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const data = await pricingApi.list();
      setItems(data);
    } catch (e) {
      toast.error("Failed to load pricing register.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const beginEdit = (item) => {
    setEditingId(item.id);
    setDraft({ minPrice: item.minPrice, maxPrice: item.maxPrice, notes: item.notes || "" });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraft({});
  };

  const save = async (item) => {
    try {
      const patch = {};
      if (Number(draft.minPrice) !== item.minPrice) patch.minPrice = Number(draft.minPrice);
      if (Number(draft.maxPrice) !== item.maxPrice) patch.maxPrice = Number(draft.maxPrice);
      if ((draft.notes || "") !== (item.notes || "")) patch.notes = draft.notes;
      if (Object.keys(patch).length === 0) {
        cancelEdit();
        return;
      }
      await pricingApi.patch(item.id, patch);
      toast.success("Pricing updated (change recorded).");
      cancelEdit();
      load();
    } catch (e) {
      const msg = e?.body?.includes("maxPrice must be >= minPrice")
        ? "maxPrice must be >= minPrice"
        : "Update failed.";
      toast.error(msg);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading pricing register&hellip;
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="pricing-register">
      <div className="rounded-sm border border-border bg-muted/40 p-3 text-xs leading-relaxed text-muted-foreground">
        Internal guide prices. Not final mandatory prices. Every change is recorded
        with the owner email and timestamp.
      </div>
      <div className="overflow-x-auto rounded-sm border border-border">
        <table className="w-full min-w-[900px] text-left text-sm">
          <thead className="bg-muted/60 text-xs uppercase tracking-[0.16em] text-muted-foreground">
            <tr>
              <th className="px-3 py-2">Service</th>
              <th className="px-3 py-2">Basis</th>
              <th className="px-3 py-2">Min</th>
              <th className="px-3 py-2">Max</th>
              <th className="px-3 py-2">Unit</th>
              <th className="px-3 py-2">Notes</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => {
              const editing = editingId === item.id;
              return (
                <tr key={item.id} className="border-t border-border align-top">
                  <td className="px-3 py-2 font-medium text-foreground">{item.label}</td>
                  <td className="px-3 py-2 text-muted-foreground">{BASIS_LABEL[item.basis] || item.basis}</td>
                  <td className="px-3 py-2">
                    {editing ? (
                      <Input
                        type="number"
                        value={draft.minPrice}
                        onChange={(e) => setDraft({ ...draft, minPrice: e.target.value })}
                        className="w-24 rounded-sm"
                        data-testid={`price-min-${item.code}`}
                      />
                    ) : (
                      <span className="text-muted-foreground">${item.minPrice}</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {editing ? (
                      <Input
                        type="number"
                        value={draft.maxPrice}
                        onChange={(e) => setDraft({ ...draft, maxPrice: e.target.value })}
                        className="w-24 rounded-sm"
                        data-testid={`price-max-${item.code}`}
                      />
                    ) : (
                      <span className="text-muted-foreground">${item.maxPrice}</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">{item.unit}</td>
                  <td className="px-3 py-2 text-xs text-muted-foreground max-w-sm">
                    {editing ? (
                      <Input
                        value={draft.notes || ""}
                        onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                        className="rounded-sm"
                      />
                    ) : (
                      item.notes
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {editing ? (
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="ghost" onClick={cancelEdit} className="rounded-sm">
                          <X className="h-4 w-4" />
                        </Button>
                        <Button size="sm" onClick={() => save(item)} className="rounded-sm" data-testid={`price-save-${item.code}`}>
                          <Save className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <Button size="sm" variant="ghost" onClick={() => beginEdit(item)} className="rounded-sm" data-testid={`price-edit-${item.code}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
